import { useEffect, useMemo, useState } from 'react';
import { api, type HandoverSource, type SettleScopePreview } from '../lib/api';
import { ErrorBox } from '../components/DataTable';
import { FitBox } from '../components/FitBox';

/**
 * 버스인 담당자 ↔ 웹 계정 연결(2026-09-27 — 담당자별 보기 범위의 기준).
 *
 * 옛 버스인에서 넘어온 접수는 담당이 **버스인 담당자 번호**로만 남아 있다. 여기서 그 사람의 웹 계정을 한 번 골라 두면
 * 옛 접수·정산도 그 사람(과 그 팀) 몫으로 갈린다. 연결 전에는 「담당 미지정」이라 범위와 무관하게 **모두에게 보인다**.
 * 이름이 정확히 같은 계정이 하나뿐이면 추천을 채워 둔다 — 「추천 그대로 연결」로 한 번에.
 */

interface LegacyRow {
  users_id: number;
  legacy_name: string | null;
  recv_cnt: number;
  last_recv: string | null;
  resign_date: string | null;
  app_user_id: number | null;
  app_user_name: string | null;
  app_user_email: string | null;
  suggest_app_user_id: number | null;
}
interface StaffRow {
  app_user_id: number;
  name: string | null;
  email: string;
  team: string | null;
}

export default function LegacyManagerMap() {
  const [rows, setRows] = useState<LegacyRow[]>([]);
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [pick, setPick] = useState<Record<number, number | ''>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.listLegacyManagers(), api.listStaff()])
      .then(([l, s]) => {
        if (cancelled) return;
        setRows(l.rows as unknown as LegacyRow[]);
        setStaff(s.rows as StaffRow[]);
        setPick({});
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const unmapped = rows.filter((r) => !r.app_user_id);
  const suggestable = useMemo(() => unmapped.filter((r) => r.suggest_app_user_id), [unmapped]);
  const label = (s: StaffRow) => `${s.name || s.email}${s.team ? ` · ${s.team}` : ''}`;

  async function save(links: { usersId: number; appUserId: number | null }[]) {
    if (links.length === 0) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const r = await api.setLegacyManagerMap({ links });
      setDone(`${r.changed}명을 연결했습니다. 「버스인접수담당자별 정산 보기」가 켜져 있으면 그 사람의 옛 접수·정산이 바로 그 사람 몫이 됩니다.`);
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const changedLinks = Object.entries(pick)
    .filter(([, v]) => v !== '')
    .map(([k, v]) => ({ usersId: Number(k), appUserId: Number(v) }));

  return (
    <section className="card space-y-3 p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-bold">
            버스인접수담당자 연결{' '}
            <span className="text-sm font-normal text-ink-soft">연결 안 됨 <b>{unmapped.length}명</b> / 전체 {rows.length}명</span>
          </h2>
          <p className="page-hint mt-0.5">
            옛 버스인 접수의 담당자를 웹 계정에 연결합니다. 연결은 아래 「버스인접수담당자별 정산 보기」를 <b>켤 때부터</b> 반영되고, 연결 안 된 담당자의 접수·정산은 <b>모두에게 보입니다</b>.
          </p>
        </div>
        <div className="btn-row-sm flex gap-2">
          {suggestable.length > 0 && (
            <button
              className="btn-ghost"
              disabled={busy}
              onClick={() => void save(suggestable.map((r) => ({ usersId: r.users_id, appUserId: r.suggest_app_user_id! })))}
            >
              이름이 같은 {suggestable.length}명 추천 그대로 연결
            </button>
          )}
          <button className="btn-primary" disabled={busy || changedLinks.length === 0} onClick={() => void save(changedLinks)}>
            고른 {changedLinks.length}명 저장
          </button>
        </div>
      </div>
      {done && <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800">{done}</div>}
      {error && <ErrorBox message={error} />}
      <div className="max-h-[28rem] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-surface">
            <tr>
              <th className="th">버스인접수담당자</th>
              <th className="th text-right">접수</th>
              <th className="th">최근 접수</th>
              <th className="th">웹 계정</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const cur = pick[r.users_id] ?? r.app_user_id ?? '';
              return (
                <tr key={r.users_id}>
                  <td className="td">
                    {r.legacy_name || `#${r.users_id}`}
                    {r.resign_date && <span className="ml-2 text-xs text-ink-faint">퇴사 {r.resign_date}</span>}
                  </td>
                  <td className="td num">{r.recv_cnt.toLocaleString()}</td>
                  <td className="td">{r.last_recv ?? '-'}</td>
                  <td className="td">
                    <select
                      className="field py-1"
                      value={cur}
                      onChange={(e) => setPick((p) => ({ ...p, [r.users_id]: e.target.value ? Number(e.target.value) : '' }))}
                    >
                      <option value="">{r.app_user_id ? '(연결 유지)' : '— 연결 안 됨 —'}</option>
                      {staff.map((s) => (
                        <option key={s.app_user_id} value={s.app_user_id}>
                          {label(s)}
                          {s.app_user_id === r.suggest_app_user_id && !r.app_user_id ? '  ← 추천' : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * 담당 넘기기(2026-10-12 way — 퇴사·인수인계). 한 사람의 담당 접수를 통째로 다른 사람에게 옮긴다.
 * 웹에서 지정한 담당과 버스인 담당 연결을 한 번에 옮기고 기록을 남긴다.
 * 🔴 거래처 마감한 달은 마감 때 담당으로 굳어 있어 실적이 따라가지 않는다(레거시와 같다) — 열린 달부터 새 담당.
 * 보기 범위는 지금 담당이라 넘겨받은 사람은 지난 미수까지 본다.
 */
export function OwnerHandoverPanel() {
  const [sources, setSources] = useState<HandoverSource[]>([]);
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [log, setLog] = useState<Awaited<ReturnType<typeof api.listOwnerHandovers>>['rows']>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof api.previewOwnerHandover>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.listHandoverSources(), api.listStaff(), api.listOwnerHandovers()])
      .then(([s, st, l]) => {
        if (cancelled) return;
        setSources(s.rows);
        setStaff(st.rows as StaffRow[]);
        setLog(l.rows);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => { cancelled = true; };
  }, [nonce]);

  // 값 = 'w:<app_user_id>' | 'l:<users_id>'
  const fromArg = (v: string) => (v.startsWith('w:') ? { appUserId: Number(v.slice(2)) } : { usersId: Number(v.slice(2)) });
  useEffect(() => {
    setPreview(null);
    if (!from) return;
    let cancelled = false;
    api.previewOwnerHandover(fromArg(from))
      .then((p) => !cancelled && setPreview(p))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => { cancelled = true; };
  }, [from]);

  const srcLabel = (s: HandoverSource) =>
    `${s.name ?? '-'}${s.team ? ` · ${s.team}` : ''}${s.kind === 'legacy' ? ' (버스인 · 웹 계정 없음)' : s.is_active === false ? ' (비활성)' : ''} — ${s.cnt.toLocaleString()}건`;
  const fromName = sources.find((s) => (s.kind === 'web' ? `w:${s.app_user_id}` : `l:${s.users_id}`) === from)?.name ?? '';
  const toRow = staff.find((s) => s.app_user_id === to);
  const total = (preview?.webCnt ?? 0) + (preview?.legacyCnt ?? 0);

  async function commit() {
    if (!from || to === '') return;
    if (!window.confirm(`${fromName} 님의 담당 접수 ${total.toLocaleString()}건을 ${toRow?.name || toRow?.email} 님에게 넘길까요?`)) return;
    setBusy(true); setError(null); setDone(null);
    try {
      const r = await api.commitOwnerHandover({ ...fromArg(from), toAppUserId: to, note: note.trim() || undefined });
      setDone(`${r.fromName} → ${r.toName}: 웹 지정 ${r.webMoved.toLocaleString()}건 · 버스인 담당 ${r.legacyMoved.toLocaleString()}건을 넘겼습니다. 마감된 달 실적은 그대로 ${r.fromName} 님 몫입니다.`);
      setFrom(''); setTo(''); setNote('');
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card space-y-3 p-5">
      <div>
        <h2 className="text-base font-bold">담당 넘기기</h2>
        <p className="page-hint mt-0.5">
          퇴사·인수인계 때 한 사람의 담당 접수를 통째로 넘깁니다(웹에서 지정한 담당 + 버스인 담당 연결). 거래처 마감한 달은 <b>그때 담당으로 굳어 있어</b> 실적이 따라가지 않고,
          아직 마감 안 된 달부터 새 담당 몫입니다. 넘겨받은 사람은 지난 달 미수까지 볼 수 있습니다. 담당 접수가 남은 계정은 비활성화할 수 없습니다.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="space-y-1 text-sm">
          <span className="block text-xs text-ink-soft">넘기는 사람</span>
          <select className="field min-w-64" value={from} onChange={(e) => setFrom(e.target.value)}>
            <option value="">— 고르세요 —</option>
            {sources.map((s) => {
              const v = s.kind === 'web' ? `w:${s.app_user_id}` : `l:${s.users_id}`;
              return <option key={v} value={v}>{srcLabel(s)}</option>;
            })}
          </select>
        </label>
        <span className="pb-2 text-ink-faint">→</span>
        <label className="space-y-1 text-sm">
          <span className="block text-xs text-ink-soft">넘겨받는 사람</span>
          <select className="field min-w-56" value={to} onChange={(e) => setTo(e.target.value ? Number(e.target.value) : '')}>
            <option value="">— 고르세요 —</option>
            {staff.filter((s) => `w:${s.app_user_id}` !== from).map((s) => (
              <option key={s.app_user_id} value={s.app_user_id}>{`${s.name || s.email}${s.team ? ` · ${s.team}` : ''}`}</option>
            ))}
          </select>
        </label>
        <input className="field w-64" placeholder="메모(예: 10월 퇴사 인수인계)" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        <button className="btn-primary" disabled={busy || !preview || total === 0 || to === ''} onClick={() => void commit()}>
          {total ? `${total.toLocaleString()}건 넘기기` : '넘기기'}
        </button>
      </div>
      {preview && (
        <div className="rounded-lg bg-canvas p-3 text-sm">
          <div>
            웹 지정 담당 <b>{preview.webCnt.toLocaleString()}건</b> · 버스인 담당 연결 <b>{preview.legacyCnt.toLocaleString()}건</b>
            {preview.frozenMonths > 0 && <span className="text-ink-soft"> · 마감돼 굳은 (접수·월) {preview.frozenMonths.toLocaleString()}개는 그대로</span>}
          </div>
          {preview.custs.length > 0 && (
            <div className="mt-1 text-xs text-ink-soft">최근 거래처: {preview.custs.map((c) => `${c.cust_name ?? '-'}(${c.recvs})`).join(' · ')}</div>
          )}
        </div>
      )}
      {done && <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800">{done}</div>}
      {error && <ErrorBox message={error} />}
      {log.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-ink-soft">넘기기 기록 {log.length}건</summary>
          <ul className="mt-2 space-y-1 text-xs">
            {log.map((l) => (
              <li key={l.owner_handover_id}>
                {l.created_at} · {l.from_name} → {l.to_name} · 웹 {l.web_moved} · 버스인 {l.legacy_recv_cnt}건{l.note ? ` · ${l.note}` : ''} <span className="text-ink-faint">({l.created_by})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

const EFF_LABEL: Record<string, string> = { own: '본인', team: '소속 팀', all: '전체' };

/**
 * 담당자별 정산 보기 — 켜기 전 미리보기 + 스위치(2026-09-27).
 *
 * 🔴 조회 범위 기본값이 「본인」이라 그냥 켜면 거의 모든 직원의 정산이 한꺼번에 좁아진다.
 * 그래서 **켜기 전에** 사람마다 「켜면 최근 3개월 접수 중 몇 건이 보이나」를 먼저 보여 준다.
 * 범위(본인·팀·전체)는 위 사용자 표에서 고친다 — 팀장은 「소속 팀」, 본부장은 「전체」.
 * 회계·관리자는 설정과 무관하게 전체다. 끄면 바로 예전(정산 전체)으로 돌아간다.
 */
export function SettleScopePanel() {
  const [data, setData] = useState<SettleScopePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.previewSettleScope()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  async function toggle(on: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.setSettleScope({ on });
      setConfirm(false);
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const rows = data?.rows ?? [];
  const narrowed = rows.filter((r) => r.eff !== 'all');
  const blind = narrowed.filter((r) => r.mine === 0 && r.eff === 'own');

  return (
    <section className="card space-y-3 p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-bold">
            버스인접수담당자별 정산 보기{' '}
            <span className={data?.on ? 'text-green-700' : 'text-ink-faint'}>{data?.on ? '켜짐' : '꺼짐'}</span>
          </h2>
          <p className="page-hint mt-0.5">
            켜면 영업관리자는 <b>본인(또는 팀)이 담당인 접수</b>의 청구·미수·영업정산과, 그 접수를 운행한 기사·차주의
            명세·재무추가·월정기·할부금만 봅니다. 회계·관리자는 늘 전체입니다. 레거시 담당 연결도 켤 때부터 접수·배차 목록에 반영됩니다.
            {' '}아래는 <b>켰을 때</b> 최근 3개월 접수 중 각자 보게 될 수입니다 — 범위는 위 사용자 표에서 고칩니다.
          </p>
          {data?.updatedBy && data.updatedBy !== 'init' && (
            <p className="mt-1 text-xs text-ink-faint">마지막 변경 {data.updatedBy} · {String(data.updatedAt ?? '').slice(0, 16)}</p>
          )}
        </div>
        <div className="btn-row-sm flex gap-2">
          {data?.on ? (
            <button className="btn-ghost" disabled={busy} onClick={() => void toggle(false)}>끄기(정산 전체로)</button>
          ) : confirm ? (
            <>
              <button className="btn-ghost" disabled={busy} onClick={() => setConfirm(false)}>취소</button>
              <button className="btn-primary" disabled={busy} onClick={() => void toggle(true)}>
                {narrowed.length}명 범위 적용 — 켜기
              </button>
            </>
          ) : (
            <button className="btn-primary" disabled={busy || !data} onClick={() => setConfirm(true)}>켜기…</button>
          )}
        </div>
      </div>
      {confirm && !data?.on && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 break-keep">
          켜면 아래 <b>{narrowed.length}명</b>의 정산 화면이 바로 좁아집니다.
          {blind.length > 0 && (
            <> 그중 <b>{blind.length}명</b>은 담당 접수가 0건이라 담당자 미지정 건만 보게 됩니다(팀장·본부장이면 범위를 먼저 바꾸세요).</>
          )}
        </div>
      )}
      {error && <ErrorBox message={error} />}
      <FitBox deps={[rows.length, confirm, !!error]} className="">
        <table className="w-full">
          <thead className="sticky top-0 bg-surface">
            <tr>
              <th className="th">사용자</th>
              <th className="th">팀</th>
              <th className="th">권한</th>
              <th className="th">정산 범위</th>
              <th className="th text-right">내 담당</th>
              <th className="th text-right">보이는 접수</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.app_user_id}>
                <td className="td">{r.name || r.email}</td>
                <td className="td">{r.team ?? '-'}</td>
                <td className="td">{r.role}</td>
                <td className="td">
                  {EFF_LABEL[r.eff] ?? r.eff}
                  {(r.role === 'admin' || r.role === 'accounting') && <span className="ml-1 text-xs text-ink-faint">(늘 전체)</span>}
                </td>
                <td className="td num">{r.mine.toLocaleString()}</td>
                <td className={`td num ${r.eff !== 'all' && r.mine === 0 ? 'text-amber-700' : ''}`}>
                  {r.visible.toLocaleString()} / {r.total.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </FitBox>
      {rows[0] && (
        <p className="text-xs text-ink-faint">
          「보이는 접수」에는 담당자 미지정 {rows[0].unassigned.toLocaleString()}건이 늘 포함됩니다 — 버스인접수담당자 연결을 채울수록 줄어듭니다.
        </p>
      )}
    </section>
  );
}
